import {
  Button,
  CloseSmIcon,
  IconButton,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  PlusMdIcon,
  SearchMdIcon,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { CSSProperties, ReactNode, useEffect, useId, useRef } from 'react';
import { cn } from 'utils/style-utils';

interface ListPickerShellProps {
  search: string;
  onSearchChange: (search: string) => void;
  onlyMine: boolean;
  onOnlyMineChange: (onlyMine: boolean) => void;
  onNew: () => void;
  //Shown as the New button's tooltip, and disables it
  newDisabledReason?: string | null;
  className?: string;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  children: ReactNode;
}

/**
 * Header (search, "My lists"), scrolling body and footer ("New") shared by the
 * list pickers, laid out like the sequence picker.
 */
const ListPickerShell = ({
  search,
  onSearchChange,
  onlyMine,
  onOnlyMineChange,
  onNew,
  newDisabledReason,
  className,
  style,
  bodyStyle,
  children,
}: ListPickerShellProps) => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onlyMineId = useId();

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const newButton = (
    <Button
      variant="solid"
      tone="gray"
      shape="pill"
      size="sm"
      className="pl-2 pr-3"
      disabled={!!newDisabledReason}
      onClick={onNew}
    >
      <PlusMdIcon className="shrink-0" />
      New
    </Button>
  );

  return (
    <div
      className={cn(
        'flex max-h-110 w-99 flex-col overflow-hidden rounded-lg border border-neutral-300 bg-white text-sm shadow-[0_4px_4px_rgba(0,0,0,0.1)]',
        className,
      )}
      style={style}
    >
      <div className="flex h-8 shrink-0 items-center justify-between gap-2 px-2 mt-2">
        <InputGroup className="h-8 min-h-8 min-w-0 flex-1" variant="text">
          <InputGroupAddon>
            <SearchMdIcon />
          </InputGroupAddon>
          <InputGroupInput
            ref={searchInputRef}
            placeholder="Search..."
            autoComplete="off"
            value={search}
            onChange={(ev) => onSearchChange(ev.target.value)}
          />
          {!!search && (
            <InputGroupAddon align="inline-end">
              <IconButton
                variant="text"
                tone="gray"
                size="xs"
                label="Clear search"
                icon={CloseSmIcon}
                onClick={() => onSearchChange('')}
              />
            </InputGroupAddon>
          )}
        </InputGroup>

        <div className="flex shrink-0 items-center gap-2">
          <label
            htmlFor={onlyMineId}
            className="whitespace-nowrap text-xs font-medium text-gray-700"
          >
            My lists
          </label>
          <Switch
            id={onlyMineId}
            checked={onlyMine}
            onCheckedChange={onOnlyMineChange}
          />
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2"
        style={bodyStyle}
      >
        {children}
      </div>

      <div className="flex shrink-0 items-center justify-end border-t border-neutral-300 p-2">
        {newDisabledReason ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{newButton}</span>
            </TooltipTrigger>
            <TooltipContent side="top" className="z-999999999">
              {newDisabledReason}
            </TooltipContent>
          </Tooltip>
        ) : (
          newButton
        )}
      </div>
    </div>
  );
};

export default ListPickerShell;
